export class ApiError extends Error {
    status;
    code;
    details;
    constructor(status, code, message, details) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.details = details;
    }
    /** Field errors from server-side validation, ready for a form library. */
    get fieldErrors() {
        const details = this.details;
        return details?.fieldErrors ?? null;
    }
    get isUnauthorized() {
        return this.status === 401;
    }
    get isForbidden() {
        return this.status === 403;
    }
    /** True when retrying later might genuinely help. */
    get isTransient() {
        return this.status >= 500 || this.status === 429;
    }
}
/**
 * Builds the request URL by string concatenation rather than `new URL()`.
 *
 * The base is often a relative path such as `/api` — the browser then sends
 * the request to the same origin, which is how the session cookie stays
 * first-party. `new URL(path, '/api/')` throws, because a relative string is
 * not a valid base, so the URL API is the wrong tool here.
 */
function buildUrl(baseUrl, path, query) {
    const base = baseUrl.replace(/\/$/, '');
    const suffix = path.startsWith('/') ? path : `/${path}`;
    const params = new URLSearchParams();
    if (query) {
        for (const [key, value] of Object.entries(query)) {
            if (value !== undefined && value !== null && value !== '') {
                params.set(key, String(value));
            }
        }
    }
    const search = params.toString();
    return search ? `${base}${suffix}?${search}` : `${base}${suffix}`;
}
export function createApiClient(options) {
    const { baseUrl, onUnauthorized, strictSchemas = false, headers: extraHeaders } = options;
    async function request(method, path, init = {}) {
        // A FormData body (a file upload) must keep its own multipart boundary —
        // JSON.stringify-ing it would throw, and setting Content-Type ourselves
        // would drop the boundary fetch generates for us.
        const isFormData = init.body instanceof FormData;
        let response;
        try {
            response = await fetch(buildUrl(baseUrl, path, init.query), {
                method,
                credentials: 'include',
                headers: {
                    ...(init.body === undefined || isFormData ? {} : { 'Content-Type': 'application/json' }),
                    ...extraHeaders?.(),
                },
                body: init.body === undefined
                    ? undefined
                    : init.body instanceof FormData
                        ? init.body
                        : JSON.stringify(init.body),
                signal: init.signal,
            });
        }
        catch (cause) {
            // A dead network is not a 500; say so plainly rather than showing a
            // server error the server never sent.
            throw new ApiError(0, 'NETWORK_ERROR', 'Could not reach the server. Check your connection.', {
                cause,
            });
        }
        if (response.status === 401) {
            onUnauthorized?.();
        }
        if (response.status === 204) {
            return undefined;
        }
        const text = await response.text();
        let payload;
        if (text) {
            try {
                payload = JSON.parse(text);
            }
            catch {
                if (!response.ok) {
                    throw new ApiError(response.status, 'BAD_RESPONSE', 'The server returned an unreadable response');
                }
            }
        }
        if (!response.ok) {
            const body = payload;
            throw new ApiError(response.status, body?.error?.code ?? 'UNKNOWN', body?.error?.message ?? 'Something went wrong', body?.error?.details);
        }
        if (init.schema) {
            const parsed = init.schema.safeParse(payload);
            if (!parsed.success) {
                // The client and server disagree about the contract.
                if (strictSchemas) {
                    throw new ApiError(response.status, 'SCHEMA_MISMATCH', `Unexpected response from ${path}`, parsed.error.issues);
                }
                return payload;
            }
            return parsed.data;
        }
        return payload;
    }
    return {
        get: (path, init) => request('GET', path, init),
        post: (path, body, init) => request('POST', path, { ...init, body }),
        postFile: (path, formData, init) => request('POST', path, { ...init, body: formData }),
        patch: (path, body, init) => request('PATCH', path, { ...init, body }),
        put: (path, body, init) => request('PUT', path, { ...init, body }),
        delete: (path, init) => request('DELETE', path, init),
    };
}
//# sourceMappingURL=api-client.js.map